import {DollarOutlined, CalendarOutlined, UsergroupAddOutlined} from "@ant-design/icons";
import {Space, Button, Tag, Tooltip, message} from "antd";
import {StyledCard, StyledClassCardContent} from "../card.style";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState} from "../../../../../redux/store";
import {LiveClassCardMenuV2} from "./card-menu";
import {handleLiveClassLink} from "../../../../../utils/live-class";
import moment from "moment";
import {LiveClassRescheduleModal} from "../../reschedule/modal";
import {useState} from "react";
import {
  buildGoogleMapsDirectionsUrl,
  launchMeeting,
  useJoinLiveEvent,
} from "../../../../../features/live-events";

export interface LiveClassCardV2Props {
  eventId: number;
  name: string;
  description: string;
  hasPhysicalLocation?: boolean;
  locationLat?: number | null;
  locationLng?: number | null;
  callPrice: number;
  callPriceCurrency: string;
  coverImage: string;
  eventDate: string;
  eventTime?: string;
  attendeesCount: number;
  statusName: string;
  isCoHost?: boolean;
  hostUsername?: string;
  slug?: string;
  tinyUrl?: string;
}

export function LiveClassCardV2({
  name,
  description,
  hostUsername,
  hasPhysicalLocation,
  locationLat,
  locationLng,
  callPrice,
  callPriceCurrency,
  coverImage,
  eventId,
  eventDate,
  eventTime,
  attendeesCount,
  statusName,
  slug,
  tinyUrl,
  isCoHost,
}: LiveClassCardV2Props) {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const navigate = useNavigate();
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const joinLiveEventMutation = useJoinLiveEvent();
  const hasVenueCoordinates = locationLat != null && locationLng != null;
  const startButtonLabel = hasPhysicalLocation
    ? "View location"
    : isCoHost
    ? "Join event"
    : "Start event";

  function handleJoinLiveClass() {
    joinLiveEventMutation.mutate(eventId, {
      onSuccess: (joinResult) => {
        const {url, target} = launchMeeting({
          joinResult,
          user: {
            firstName: userInfo?.user?.firstName ?? "",
            lastName: userInfo?.user?.lastName ?? "",
          },
          event: {name},
          role: isCoHost ? "guest" : "host",
        });
        setTimeout(() => window.open(url, target), 0);
      },
    });
  }

  function handleViewLocation() {
    if (locationLat == null || locationLng == null) return;
    message.info("Redirecting you to Google Maps...");
    // Open synchronously: a delayed window.open loses the click's user
    // gesture and Safari blocks it as a popup.
    window.open(buildGoogleMapsDirectionsUrl(locationLat, locationLng), "_blank");
  }

  const stripedHtml = description.replace(/<[^>]+>/g, "");
  const eventDescription = stripedHtml;

  const classDate = eventDate ? moment(eventDate).format("ddd, MMM DD YYYY") : "TBD";
  const classTime = eventTime ?? "TBD";

  const showDrawer = () => {
    setOpenRescheduleModal(true);
  };

  const onClose = () => {
    setOpenRescheduleModal(false);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button, a, .ant-dropdown-trigger")) {
      return;
    }

    if (isCoHost && hostUsername) {
      window.open(
        handleLiveClassLink({
          eventId,
          slug: slug ?? "",
          tinyUrl,
          username: hostUsername,
          windowNav: true,
        }),
        "_blank"
      );
    } else {
      navigate(`/live-classes/edit/${eventId}?activeTab=form`);
    }
  };

  return (
    <>
      <StyledCard
        title={name}
        {...(!isCoHost && {
          extra: (
            <LiveClassCardMenuV2
              eventId={eventId}
              isCoHost={isCoHost}
              hostUsername={hostUsername}
              slug={slug}
              tinyUrl={tinyUrl}
            />
          ),
        })}
      >
        <StyledClassCardContent onClick={handleCardClick}>
          <div className="class-card-image-wrapper">
            <img className="class-card-image" src={coverImage} alt={name} />
          </div>
          <p className="class-card-info-text">{eventDescription}</p>
          <div className="class-card-extra-info">
            <div>
              <Space size={10}>
                <DollarOutlined style={{color: "#222222"}} />
                <span>
                  {callPrice === 0
                    ? "Free"
                    : callPriceCurrency + " " + callPrice.toLocaleString("en-US")}
                </span>
              </Space>
            </div>
            <div>
              <Space size={10}>
                <CalendarOutlined style={{color: "#222222"}} />
                <span>
                  {classDate}, {classTime}
                </span>
              </Space>
            </div>
            <div>
              <Space size={10}>
                <UsergroupAddOutlined style={{color: "#222222"}} />
                <span>{attendeesCount} attending</span>
              </Space>
            </div>
          </div>
          <div className="reg-tag">
            {isCoHost && (
              <div>
                <Tag color="green">co-host</Tag>
              </div>
            )}
            <div>{statusName === "Paused" && <Tag>Registration closed</Tag>}</div>
          </div>

          <Tooltip title={startButtonLabel}>
            <div className="class-card-start-btn">
              <Button
                onClick={hasPhysicalLocation ? handleViewLocation : handleJoinLiveClass}
                type="primary"
                block
                size="small"
                disabled={hasPhysicalLocation && !hasVenueCoordinates}
                loading={!hasPhysicalLocation && joinLiveEventMutation.isLoading}
              >
                {startButtonLabel}
              </Button>
            </div>
          </Tooltip>
          {!isCoHost && (
            <div className="class-card-details-btn">
              <Button onClick={showDrawer} block size="small">
                Reschedule Event
              </Button>
            </div>
          )}
        </StyledClassCardContent>
      </StyledCard>
      {openRescheduleModal && (
        <LiveClassRescheduleModal
          eventId={eventId}
          onClose={onClose}
          visible={openRescheduleModal}
        />
      )}
    </>
  );
}
